import { useId, useState } from 'react'

import { Alert, Button } from '@atoms'
import Badge from '@components/atoms/Badge'
import { Progress } from '@components/atoms/Progress'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

import {
  FILE_RETENTION_DAYS,
  type ImportRun,
  isSettled,
  REVIEWED_ON,
  REVIEWER,
  rowsToLookAt,
  summaryPath,
  TYPE_LABELS,
} from './sampleData'
import { AlertText, Card, CardHeading, CountTile, inlineLinkClasses, ReviewedBadge, StatusBadge } from './ui'

export type Viewer = 'admin' | 'captain'

export type ImportSummaryProps = {
  run: ImportRun
  viewer: Viewer
  /** False once P5.4 has purged the uploaded file. */
  fileAvailable?: boolean
  /** Inside step 3 of the guided flow, the summary also says where to find itself later. */
  embedded?: boolean
  /** The optional "mark as reviewed" extension. Absent when the extension is off. */
  review?: Review
  /** The instance's SHOW_EMAIL setting is off, so emails show as m***@e***, as everywhere else in the app. */
  maskEmails?: boolean
  /** Only the person who ran the import and admins get the full rows back, since they carry unmasked emails. */
  canDownloadRows?: boolean
}

export type Review = { reviewed: boolean; onChange: (reviewed: boolean) => void }

export const FAILURES_ID = 'failures'

/** Mirrors Maskers::Email. A name with no @ is shown as it is. */
const maskEmail = (identifier: string) => {
  const [local, domain] = identifier.split('@')
  return domain === undefined ? identifier : `${local[0]}***@${domain[0]}***`
}

/**
 * The import summary: what SupporterBase did with the file, as opposed to what
 * CSVBox accepted. Shows a working run that is still processing rows, then
 * settles into the final counts and the per-row failures (story 18).
 */
export function ImportSummary({
  run,
  viewer,
  fileAvailable = true,
  embedded = false,
  review,
  maskEmails = false,
  canDownloadRows = true,
}: ImportSummaryProps) {
  const settled = isSettled(run.status)

  return (
    <div className="tw:flex tw:flex-col tw:gap-5">
      {settled ? <SettledSummary run={run} /> : <WorkingSummary run={run} />}

      {embedded && settled && (
        <Alert type="info" size="sm" className="tw:text-sm">
          <AlertText>
            This summary stays at{' '}
            <a href={summaryPath(run)} className={inlineLinkClasses}>
              {summaryPath(run)}
            </a>
            , and we have emailed it to you.
          </AlertText>
        </Alert>
      )}

      {run.outcomes.length > 0 && (
        <FailuresTable
          run={run}
          settled={settled}
          fileAvailable={fileAvailable}
          review={review}
          maskEmails={maskEmails}
          canDownload={fileAvailable && canDownloadRows}
        />
      )}

      {viewer === 'admin' && <AdminFile run={run} fileAvailable={fileAvailable} />}
    </div>
  )
}

function RunMeta({ run }: { run: ImportRun }) {
  return (
    <p className="tw:text-sm tw:text-gray-cool-600">
      {run.fileName} · {run.submittedAt} · by {run.importer}
    </p>
  )
}

/** What the run was for. Facts about the run, so a label and value each rather than badges, which read as status. */
function RunDetails({ run }: { run: ImportRun }) {
  const details = [
    { label: 'Import type', value: TYPE_LABELS[run.type] },
    { label: 'Group', value: run.group },
    run.event && { label: 'Event', value: run.event },
    run.type === 'event_rsvps' && run.markAsAttended && { label: 'Attendance', value: 'Everyone marked as attended' },
  ].filter((detail): detail is { label: string; value: string } => Boolean(detail))

  return (
    <dl className="tw:m-0 tw:flex tw:flex-wrap tw:gap-x-8 tw:gap-y-3">
      {details.map((detail) => (
        <div key={detail.label} className="tw:flex tw:flex-col tw:gap-0.5">
          <dt className="tw:text-xs tw:font-normal tw:text-gray-cool-600">{detail.label}</dt>
          <dd className="tw:m-0 tw:text-sm tw:font-medium tw:text-gray-cool-900">{detail.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function WorkingSummary({ run }: { run: ImportRun }) {
  const queued = run.status === 'queued'
  const percent = Math.round((run.processedRowCount / run.submittedRowCount) * 100)

  return (
    <Card>
      <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-3 tw:border-b tw:border-gray-200 tw:px-5 tw:py-4">
        <span
          aria-hidden
          className="tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary-50 tw:text-primary-600"
        >
          <Icon
            type="refresh"
            className={cn('tw:h-5 tw:w-5', !queued && 'tw:animate-spin tw:motion-reduce:animate-none')}
          />
        </span>
        <div className="tw:min-w-0 tw:flex-1">
          <h2 className="tw:text-base tw:font-bold tw:text-gray-cool-900">
            {queued ? 'Waiting to start' : 'Importing your file'}
          </h2>
          <RunMeta run={run} />
        </div>
        <StatusBadge status={run.status} />
      </div>

      {/* Polite live region: the counter changes as rows are processed. */}
      <div role="status" className="tw:flex tw:flex-col tw:gap-2 tw:px-5 tw:py-4">
        <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-4 tw:text-sm">
          <span className="tw:font-medium tw:text-gray-cool-900">
            {queued
              ? `${run.submittedRowCount.toLocaleString()} rows submitted`
              : `${run.processedRowCount.toLocaleString()} of ${run.submittedRowCount.toLocaleString()} rows processed`}
          </span>
          {!queued && <span className="tw:tabular-nums tw:text-gray-cool-600">{percent}%</span>}
        </div>
        <Progress
          value={queued ? 0 : percent}
          progressColor="tw:bg-primary-600"
          className="tw:bg-gray-cool-100"
          aria-label="Rows processed"
        />
        <p className="tw:text-xs tw:text-gray-cool-600">
          {queued
            ? 'Another import is running ahead of this one. Yours starts as soon as it finishes.'
            : 'Supporters are added one at a time. You can close this page; we will email you when it finishes.'}
        </p>
      </div>

      <div className="tw:grid tw:grid-cols-2 tw:divide-x tw:divide-gray-100 tw:border-t tw:border-gray-100 tw:sm:grid-cols-4">
        <CountTile value={run.counts.created} label="created so far" muted={queued} />
        <CountTile value={run.counts.updated} label="updated so far" muted={queued} />
        <CountTile value={run.counts.failed} label="failed so far" tone="error" muted={queued} />
        <CountTile value={run.counts.skipped} label="skipped so far" tone="warning" muted={queued} />
      </div>

      <div className="tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
        <RunDetails run={run} />
      </div>
    </Card>
  )
}

function SettledSummary({ run }: { run: ImportRun }) {
  const toReview = rowsToLookAt(run.counts)
  const failedRun = run.status === 'failed'
  const { created, updated, failed, skipped } = run.counts

  const heading = failedRun
    ? 'The import could not start'
    : toReview === 0
      ? 'Import finished'
      : `Import finished, with ${toReview} ${toReview === 1 ? 'row' : 'rows'} to review`

  const iconTone = failedRun
    ? 'tw:bg-error-50 tw:text-error-600'
    : toReview === 0
      ? 'tw:bg-success-50 tw:text-success-600'
      : 'tw:bg-warning-50 tw:text-warning-600'

  return (
    <Card>
      <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-3 tw:border-b tw:border-gray-200 tw:px-5 tw:py-4">
        <span
          aria-hidden
          className={cn(
            'tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full',
            iconTone
          )}
        >
          <Icon
            type={failedRun ? 'x-circle' : toReview === 0 ? 'check-circle' : 'alert-triangle'}
            className="tw:h-5 tw:w-5"
          />
        </span>
        <div className="tw:min-w-0 tw:flex-1">
          <h2 className="tw:text-base tw:font-bold tw:text-gray-cool-900">{heading}</h2>
          <RunMeta run={run} />
        </div>
        <StatusBadge status={run.status} />
      </div>

      <div className="tw:grid tw:grid-cols-2 tw:divide-x tw:divide-gray-100 tw:sm:grid-cols-4">
        <CountTile value={created} label={run.type === 'event_rsvps' ? 'RSVPs created' : 'supporters created'} />
        <CountTile value={updated} label={run.type === 'event_rsvps' ? 'RSVPs updated' : 'supporters updated'} />
        <CountTile value={failed} label="rows failed" tone="error" />
        <CountTile value={skipped} label="rows skipped" tone="warning" />
      </div>

      {/* The reconciliation line is the trust signal: the counts always add up to what was submitted. */}
      <p className="tw:border-t tw:border-gray-100 tw:px-5 tw:py-3 tw:text-xs tw:tabular-nums tw:text-gray-cool-600">
        {created.toLocaleString()} created + {updated.toLocaleString()} updated + {failed} failed + {skipped} skipped ={' '}
        <span className="tw:font-medium tw:text-gray-cool-900">
          {run.processedRowCount.toLocaleString()} of {run.submittedRowCount.toLocaleString()} rows submitted
        </span>
      </p>

      {failedRun && (
        <div className="tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
          <Alert type="error" title="The import could not start">
            <AlertText>
              Nothing in the file was imported. Upload it again, and contact support if this happens twice.
            </AlertText>
          </Alert>
        </div>
      )}

      <div className="tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
        <RunDetails run={run} />
      </div>
    </Card>
  )
}

function FailuresTable({
  run,
  settled,
  fileAvailable,
  review,
  maskEmails,
  canDownload,
}: {
  run: ImportRun
  settled: boolean
  fileAvailable: boolean
  review?: Review
  maskEmails: boolean
  canDownload: boolean
}) {
  const toReview = rowsToLookAt(run.counts)
  const reviewed = settled && review?.reviewed
  const [showRows, setShowRows] = useState(false)
  const rowsId = useId()
  const rowsVisible = !reviewed || showRows

  const setReviewed = (next: boolean) => {
    setShowRows(false)
    review?.onChange(next)
  }

  return (
    <Card id={FAILURES_ID}>
      <CardHeading
        icon={reviewed ? 'check-circle' : 'alert-triangle'}
        title={
          reviewed
            ? `${toReview} ${toReview === 1 ? 'row' : 'rows'} reviewed`
            : settled
              ? `${toReview} ${toReview === 1 ? 'row' : 'rows'} to review`
              : 'Rows to review so far'
        }
        subtitle={
          fileAvailable
            ? reviewed
              ? 'Row numbers match your file.'
              : 'Row numbers match your file. Fix these rows and upload only them.'
            : `The rows themselves were removed ${FILE_RETENTION_DAYS} days after the import. Each row's number, email and reason are kept.`
        }
        actions={
          reviewed ? (
            <ReviewedBadge />
          ) : (
            (canDownload || (settled && review)) && (
              <>
                {settled && review && (
                  <Button rank="secondary" size="sm" leadingIcon="check" onClick={() => setReviewed(true)}>
                    Mark as reviewed
                  </Button>
                )}
                {canDownload && (
                  <Button rank="secondary" size="sm" leadingIcon="file-download-03" disabled={!settled}>
                    Download these rows
                  </Button>
                )}
              </>
            )
          )
        }
      />

      {reviewed && (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:px-5 tw:py-3">
          <p className="tw:mb-0 tw:flex tw:flex-wrap tw:items-center tw:gap-x-2 tw:text-sm tw:text-gray-cool-600">
            Marked as reviewed by {REVIEWER} on {REVIEWED_ON}.
            <button type="button" className={inlineLinkClasses} onClick={() => setReviewed(false)}>
              Undo
            </button>
          </p>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
            {canDownload && (
              <Button rank="secondary" size="sm" leadingIcon="file-download-03">
                Download these rows
              </Button>
            )}
            <Button
              rank="link"
              size="sm"
              trailingIcon={showRows ? 'chevron-down' : 'chevron-right'}
              aria-expanded={showRows}
              aria-controls={rowsId}
              onClick={() => setShowRows(!showRows)}
            >
              {showRows ? 'Hide the rows' : `Show the ${toReview} ${toReview === 1 ? 'row' : 'rows'}`}
            </Button>
          </div>
        </div>
      )}

      {!settled && (
        <p className="tw:border-b tw:border-gray-100 tw:px-5 tw:py-2 tw:text-xs tw:text-gray-cool-600">
          More may appear while the import runs. The download is ready once it finishes.
        </p>
      )}

      {rowsVisible && (
        <div id={rowsId} className={cn(reviewed && 'tw:border-t tw:border-gray-100')}>
          <div className="tw:overflow-x-auto">
            <table className="tw:min-w-full tw:border-collapse tw:text-sm">
              <thead>
                <tr className="tw:bg-gray-cool-50 tw:text-left tw:text-xs tw:text-gray-cool-600">
                  <th scope="col" className="tw:w-16 tw:border-b tw:border-gray-200 tw:px-5 tw:py-2 tw:font-medium">
                    Row
                  </th>
                  <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                    Email or name
                  </th>
                  <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                    What happened
                  </th>
                  <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                    Why
                  </th>
                </tr>
              </thead>
              <tbody>
                {run.outcomes.map((outcome) => (
                  <tr key={outcome.row} className="tw:odd:bg-white tw:even:bg-gray-cool-50/40">
                    <td className="tw:px-5 tw:py-2.5 tw:tabular-nums tw:text-gray-cool-600">{outcome.row}</td>
                    <td className="tw:px-3 tw:py-2.5 tw:font-medium tw:text-gray-cool-900">
                      {maskEmails ? maskEmail(outcome.identifier) : outcome.identifier}
                    </td>
                    <td className="tw:px-3 tw:py-2.5">
                      <Badge color={outcome.kind === 'failed' ? 'error' : 'warning'} shape="rounded">
                        {outcome.kind === 'failed' ? 'Failed' : 'Skipped'}
                      </Badge>
                    </td>
                    <td className="tw:px-3 tw:py-2.5 tw:text-gray-cool-700">{outcome.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="tw:border-t tw:border-gray-100 tw:px-5 tw:py-3 tw:text-xs tw:text-gray-cool-600">
            <span className="tw:font-medium tw:text-gray-cool-900">Failed</span> rows were not imported.{' '}
            <span className="tw:font-medium tw:text-gray-cool-900">Skipped</span> rows were left alone on purpose, for
            example a duplicate of an earlier row.
          </p>
        </div>
      )}
    </Card>
  )
}

function AdminFile({ run, fileAvailable }: { run: ImportRun; fileAvailable: boolean }) {
  return (
    <Card>
      <CardHeading
        icon="file-05"
        title="The uploaded file"
        subtitle={
          fileAvailable
            ? `Only admins can download it. It is removed ${FILE_RETENTION_DAYS} days after the import.`
            : `Removed ${FILE_RETENTION_DAYS} days after the import. The counts and the rows above are kept.`
        }
        actions={
          fileAvailable ? (
            <Button rank="secondary" size="sm" leadingIcon="download-cloud-02">
              Download file
            </Button>
          ) : (
            <Badge color="gray" shape="rounded" leadingIcon="trash-01">
              File removed
            </Badge>
          )
        }
      />
      <dl className="tw:grid tw:gap-3 tw:px-5 tw:py-4 tw:text-sm tw:sm:grid-cols-3">
        <div>
          <dt className="tw:text-xs tw:text-gray-cool-600">File</dt>
          <dd className="tw:font-medium tw:text-gray-cool-900">{run.fileName}</dd>
        </div>
        <div>
          <dt className="tw:text-xs tw:text-gray-cool-600">Rows</dt>
          <dd className="tw:font-medium tw:tabular-nums tw:text-gray-cool-900">
            {run.submittedRowCount.toLocaleString()}
          </dd>
        </div>
        <div>
          <dt className="tw:text-xs tw:text-gray-cool-600">Uploaded by</dt>
          <dd className="tw:font-medium tw:text-gray-cool-900">{run.importer}</dd>
        </div>
      </dl>
    </Card>
  )
}
